/**
 * Water-resource report section fallbacks (SNT-5).
 *
 * Pure deterministic fallbacks for the free-text fields on the
 * `water-resource` report-type schema (siteRag.ts:1721-1732). Each
 * composer reads only from `sed.waterStress`, `sed.climate`,
 * `sed.onSiteInfrastructure.waterFeatures` — no AI calls, no I/O.
 *
 * Contract & composition mirror `feasibility.ts`. The recursion guard
 * R6 is pinned by `instructionShapeGuard.test.ts -> "fallback recursion
 * guard"`.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';

/* ───────────────────────── helpers ──────────────────────────── */

/**
 * WRI Aqueduct baseline descriptor — "level (score / 5)" — or `[—]`
 * when the slot is unavailable.
 */
function wriAqueductLabel(sed: SiteEvidenceDocument): string {
  const w = sed.waterStress?.data;
  if (!w) return '[—]';
  const stress = w.stressLevel ?? '[—]';
  if (typeof w.bwsScore === 'number' && Number.isFinite(w.bwsScore)) {
    return `${stress} (${w.bwsScore.toFixed(1)} / 5)`;
  }
  return `${stress} (score [—])`;
}

/** Surface-water proximity from KMZ-derived water features. */
function surfaceWaterLabel(sed: SiteEvidenceDocument): string {
  const wf = sed.onSiteInfrastructure?.data?.waterFeatures;
  if (!Array.isArray(wf) || wf.length === 0) return '[—]';
  const closest = wf.reduce((acc, cur) =>
    typeof cur.distanceKm === 'number' && Number.isFinite(cur.distanceKm) &&
    (acc === null || cur.distanceKm < acc.distanceKm) ? cur : acc,
    null as null | typeof wf[number],
  );
  if (!closest) return `${wf.length} KMZ-tagged water feature(s)`;
  const kind = closest.kind ?? 'unknown';
  const distLabel =
    typeof closest.distanceKm === 'number' && Number.isFinite(closest.distanceKm)
      ? `${closest.distanceKm.toFixed(1)} km`
      : '[—]';
  return `${wf.length} water feature(s); closest ${kind} at ${distLabel}`;
}

/* ───────────────────────── composers ────────────────────────── */

/**
 * Water-resource executive summary fallback.
 */
export function waterExecutiveSummaryFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push(`${site.name} water-resource summary.`);
  parts.push(`WRI Aqueduct baseline water stress: ${wriAqueductLabel(sed)}.`);
  const sw = surfaceWaterLabel(sed);
  if (sw !== '[—]') parts.push(`Surface-water proximity: ${sw}.`);
  const cd = sed.climate?.data;
  if (cd && typeof cd.annualPrecipitation === 'number' && Number.isFinite(cd.annualPrecipitation)) {
    parts.push(`Annual precipitation ${cd.annualPrecipitation.toFixed(0)} mm.`);
  }
  parts.push('Detailed narrative pending re-run.');
  return parts.join(' ');
}

/**
 * Supply availability narrative fallback. Composes WRI + precipitation +
 * surface-water proximity.
 */
export function supplyAvailabilityFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push(`WRI Aqueduct baseline: ${wriAqueductLabel(sed)}.`);
  const cd = sed.climate?.data;
  if (cd && typeof cd.annualPrecipitation === 'number' && Number.isFinite(cd.annualPrecipitation)) {
    parts.push(`Annual precipitation ${cd.annualPrecipitation.toFixed(0)} mm.`);
  }
  const sw = surfaceWaterLabel(sed);
  if (sw !== '[—]') parts.push(`Nearby water bodies: ${sw}.`);
  parts.push('Supply yield modelling pending re-run.');
  return parts.join(' ');
}

/**
 * Groundwater assessment fallback. The SED does not carry a groundwater
 * provider today; render `[—]` and surface the proxy indicators.
 */
export function groundwaterAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push('Groundwater table depth: [—].');
  const ws = sed.waterStress?.data;
  if (ws && typeof ws.bwsRaw === 'number' && Number.isFinite(ws.bwsRaw)) {
    parts.push(`Withdrawal-to-supply ratio ${ws.bwsRaw.toFixed(2)}.`);
  }
  parts.push('Aquifer detail pending re-run.');
  return parts.join(' ');
}

/**
 * Surface-water assessment fallback. Composes from KMZ-derived water
 * features + climate.
 */
export function surfaceWaterAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const sw = surfaceWaterLabel(sed);
  if (sw !== '[—]') parts.push(`KMZ inventory: ${sw}.`);
  else parts.push('KMZ-tagged water features: [—].');
  const fz = sed.flood?.data?.floodZone;
  if (typeof fz === 'string' && fz.length > 0) parts.push(`FEMA zone ${fz}.`);
  parts.push('Flow-rate and water-rights detail pending re-run.');
  return parts.join(' ');
}

/**
 * Water rights analysis fallback (water-resource schema).
 */
export function waterRightsAnalysisFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const reg = sed.regulatory?.data;
  if (reg?.state) parts.push(`Water-rights regime keyed to state ${reg.state}.`);
  parts.push(`WRI baseline: ${wriAqueductLabel(sed)}.`);
  parts.push('Permit application timeline pending re-run.');
  return parts.join(' ');
}

/**
 * Drought resilience narrative fallback. Reads climate + WRI.
 */
export function droughtResilienceFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const cd = sed.climate?.data;
  if (cd) {
    if (typeof cd.annualPrecipitation === 'number' && Number.isFinite(cd.annualPrecipitation)) {
      parts.push(`Annual precipitation ${cd.annualPrecipitation.toFixed(0)} mm`);
    }
    if (typeof cd.avgTemp === 'number' && Number.isFinite(cd.avgTemp)) {
      parts.push(`average ${cd.avgTemp.toFixed(1)} C`);
    }
  }
  const ws = sed.waterStress?.data;
  if (ws) parts.push(`WRI stress ${ws.stressLevel ?? '[—]'}`);
  if (parts.length === 0) return 'Drought resilience narrative pending re-run.';
  return `Climate-stress indicators: ${parts.join('; ')}. Resilience narrative pending re-run.`;
}

/**
 * Cooling-water requirements narrative fallback. The SED does not yet
 * carry workload-specific cooling demand; surface peak wet-bulb as the
 * dominant indicator.
 */
export function coolingWaterRequirementsFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const cd = sed.climate?.data;
  const parts: string[] = [];
  if (cd) {
    if (typeof cd.peakWetBulb === 'number' && Number.isFinite(cd.peakWetBulb)) {
      parts.push(`highest monthly wet bulb ${cd.peakWetBulb.toFixed(1)} C (historical screening, not an equipment design condition)`);
    }
    if (typeof cd.peakTemp === 'number' && Number.isFinite(cd.peakTemp)) {
      parts.push(`peak dry bulb ${cd.peakTemp.toFixed(1)} C`);
    }
  }
  if (parts.length === 0) return 'Cooling-water requirements narrative pending re-run.';
  return `Climate baseline: ${parts.join('; ')}. Make-up-water rate calculation pending re-run.`;
}

/**
 * Wastewater management narrative fallback. SED lacks a POTW provider
 * today; render `[—]` and surface the proxy indicators.
 */
export function wastewaterManagementFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'POTW capacity and tie-in distance: [—]. Wastewater discharge pathway pending re-run.';
}

/**
 * Water-resource conclusion recommendation fallback.
 */
export function waterRecommendationFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Water-resource recommendation pending re-run.';
}
